import { api } from './client'

/* F-089 (TASK-0343): coda backoffice. Eccezioni (F-029), SLA (F-031) e regole di priorità. Nessun importo. */

export type ExceptionSeverity = 'Bloccante' | 'Avviso'

export type ExceptionKind =
  | 'CampoMancante'
  | 'DocumentoMancante'
  | 'DocumentoScaduto'
  | 'IbanNonValido'
  | 'IncongruenzaDati'
  | 'Duplicato'
  | 'ProvvigioneAnomala'

export const EXCEPTION_KIND_LABELS: Record<ExceptionKind, string> = {
  CampoMancante: 'Campo mancante',
  DocumentoMancante: 'Documento mancante',
  DocumentoScaduto: 'Documento scaduto',
  IbanNonValido: 'Dato bancario non valido',
  IncongruenzaDati: 'Dati incoerenti',
  Duplicato: 'Duplicato',
  ProvvigioneAnomala: 'Provvigione anomala',
}

/** F-105: la presa in carico di un'eccezione (decisione ancora in vigore). */
export interface ExceptionReview {
  decision: 'PresaInCarico' | 'Ignorata'
  actorUserId: number | null
  decidedAt: string
  note: string | null
}

/** F-105: filtro della presa in carico. Predefinito (assente): tutto tranne le ignorate. */
export type ExceptionReviewFilter = 'open' | 'taken' | 'ignored' | 'all'

export interface ContractExceptionItem {
  contractId: number
  referenceCode: string
  kind: ExceptionKind
  severity: ExceptionSeverity
  label: string
  description: string
  ruleCode: string | null
  review?: ExceptionReview | null
}

export interface ContractExceptionAggregateRow {
  kind: ExceptionKind
  ruleCode: string | null
  label: string
  severity: ExceptionSeverity
  affectedContracts: number
}

export interface ContractExceptionsResponse {
  type: string | null
  severity: string | null
  limit: number
  aggregate: ContractExceptionAggregateRow[]
  /** F-105: eccezioni ignorate (con presa in carico in vigore) fra quelle del tipo/gravità richiesti. */
  ignoredCount?: number
  total: number
  hasMore: boolean
  count: number
  items: ContractExceptionItem[]
}

export interface ContractSlaAggregateRow {
  status: string
  count: number
  hasSla: boolean
  compliant: number
  breached: number
  noHistory: number
  /** F-094: soglia in vigore per lo stato e da dove viene. */
  thresholdHours?: number | null
  thresholdSource?: 'default' | 'policy'
  policyId?: number | null
}

export interface ContractSlaItem {
  contractId: number
  referenceCode: string
  status: string
  agentId: number | null
  currentPriority: string
  statusSince: string | null
  hasSla: boolean
  thresholdHours: number | null
  /** F-094: 'default' = valore di serie, 'policy' = politica attiva (policyId). */
  thresholdSource?: 'default' | 'policy'
  policyId?: number | null
  elapsedHours: number | null
  isBreached: boolean
  hoursOverdue: number
  /** "Xh Ym" quando la pratica ha superato la soglia; null altrimenti. */
  overdueLabel: string | null
  suggestedPriority: string | null
  explanation: string
}

export interface ContractSlaResponse {
  status: string | null
  onlyOverdue: boolean
  limit: number
  aggregate: ContractSlaAggregateRow[]
  total: number
  hasMore: boolean
  count: number
  items: ContractSlaItem[]
}

export type PriorityValue = 'low' | 'normal' | 'high' | 'urgent'

export const PRIORITY_LABELS: Record<string, string> = {
  low: 'Bassa',
  normal: 'Normale',
  high: 'Alta',
  urgent: 'Urgente',
}

/** Gli ultimi sono gli stati del ciclo di vita uniforme (R2-REGOLE); Disattivata vale come Pubblicata. */
export type PriorityRuleStatus =
  | 'Bozza' | 'Attiva' | 'Disattivata' | 'Test' | 'Simulazione' | 'Approvazione' | 'Pubblicata' | 'Archiviata'

export interface PriorityCondition {
  field: string
  operator: string
  value: string
}

export interface PriorityRule {
  id: number
  name: string
  conditions: PriorityCondition[]
  targetPriority: PriorityValue
  order: number
  status: PriorityRuleStatus
  createdAt: string
  updatedAt: string | null
}

export interface PriorityRulesResponse {
  rules: PriorityRule[]
  attributes: string[]
  operators: string[]
  priorities: PriorityValue[]
}

export interface PriorityRuleInput {
  name: string
  conditions: PriorityCondition[]
  targetPriority: PriorityValue
  order: number
}

export interface PriorityRulePreview {
  ruleId: number
  ruleStatus: PriorityRuleStatus
  targetPriority: PriorityValue
  examined: number
  matching: number
  wouldChange: number
  byTargetPriority: Record<string, number>
  skippedManual: number
  sampleContractIds: number[]
  sampleLimit: number
}

/* F-094 (TASK-0348): soglie SLA configurabili per stato. */

export type SlaPolicyStatus = 'Bozza' | 'Attiva' | 'Disattivata'

export interface SlaStatusThreshold {
  status: string
  hasSla: boolean
  thresholdHours: number | null
  thresholdSource: 'default' | 'policy'
  policyId: number | null
  defaultThresholdHours: number | null
}

export interface SlaPolicy {
  id: number
  contractStatus: string
  hasSla: boolean
  thresholdHours: number | null
  status: SlaPolicyStatus
  reason: string | null
  createdAt: string
  updatedAt: string | null
  activatedAt: string | null
}

export interface SlaPoliciesResponse {
  statuses: SlaStatusThreshold[]
  policies: SlaPolicy[]
  noSlaStatuses: string[]
  minThresholdHours: number
  maxThresholdHours: number
}

export interface SlaPolicyInput {
  contractStatus: string
  hasSla: boolean
  thresholdHours: number | null
}

export interface SlaPolicyPreview {
  policyId: number
  policyStatus: SlaPolicyStatus
  contractStatus: string
  examined: number
  breachedNow: number
  breachedWithPolicy: number
  newlyBreached: number
  noLongerBreached: number
  newlyBreachedSampleIds: number[]
  noLongerBreachedSampleIds: number[]
  sampleLimit: number
}

/** Le eccezioni di una singola pratica (GET /api/contracts/{id}/exceptions). */
export interface ContractExceptionsOfOne {
  contractId: number
  exceptions: ContractExceptionItem[]
}

/** Priorità di una pratica con la sua origine (dalla scheda pratica, GET /api/contracts/{id}). */
export interface ContractPriorityInfo {
  priority: string
  prioritySource?: 'manual' | 'rule' | null
  priorityRuleId?: number | null
}

export const backofficeQueueApi = {
  exceptions: (p: { type?: string; severity?: string; limit?: number; review?: ExceptionReviewFilter } = {}) => {
    const q = new URLSearchParams()
    if (p.type) q.set('type', p.type)
    if (p.severity) q.set('severity', p.severity)
    if (p.review) q.set('review', p.review)
    if (p.limit) q.set('limit', String(p.limit))
    return api<ContractExceptionsResponse>(`/api/contracts/exceptions?${q}`)
  },

  sla: (p: { status?: string; onlyOverdue?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (p.status) q.set('status', p.status)
    if (p.onlyOverdue) q.set('onlyOverdue', 'true')
    if (p.limit) q.set('limit', String(p.limit))
    return api<ContractSlaResponse>(`/api/contracts/sla?${q}`)
  },

  /** F-105: prende in carico o ignora (nota obbligatoria) un'eccezione di una pratica. */
  reviewException: (
    id: number,
    body: { kind: ExceptionKind; ruleCode?: string | null; decision: 'presa_in_carico' | 'ignorata'; note?: string },
  ) => api<unknown>(`/api/contracts/${id}/exceptions/review`, { method: 'POST', body: JSON.stringify(body) }),

  /** F-105: "Riapri" — toglie la decisione. */
  reopenException: (id: number, kind: ExceptionKind, ruleCode?: string | null) => {
    const q = new URLSearchParams({ kind })
    if (ruleCode) q.set('ruleCode', ruleCode)
    return api<void>(`/api/contracts/${id}/exceptions/review?${q}`, { method: 'DELETE' })
  },

  contractExceptions: (id: number) => api<ContractExceptionsOfOne>(`/api/contracts/${id}/exceptions`),

  contractSla: (id: number) => api<ContractSlaItem>(`/api/contracts/${id}/sla`),

  contractPriority: (id: number) => api<ContractPriorityInfo>(`/api/contracts/${id}`),

  rules: () => api<PriorityRulesResponse>('/api/contracts/priority-rules'),

  createRule: (input: PriorityRuleInput) =>
    api<PriorityRule>('/api/contracts/priority-rules', { method: 'POST', body: JSON.stringify(input) }),

  updateRule: (id: number, input: PriorityRuleInput) =>
    api<PriorityRule>(`/api/contracts/priority-rules/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  previewRule: (id: number) => api<PriorityRulePreview>(`/api/contracts/priority-rules/${id}/preview`),

  activateRule: (id: number) =>
    api<PriorityRule>(`/api/contracts/priority-rules/${id}/activate`, { method: 'POST' }),

  deactivateRule: (id: number) =>
    api<PriorityRule>(`/api/contracts/priority-rules/${id}/deactivate`, { method: 'POST' }),

  deleteRule: (id: number) => api<void>(`/api/contracts/priority-rules/${id}`, { method: 'DELETE' }),

  slaPolicies: () => api<SlaPoliciesResponse>('/api/contracts/sla-policies'),

  createSlaPolicy: (input: SlaPolicyInput) =>
    api<SlaPolicy>('/api/contracts/sla-policies', { method: 'POST', body: JSON.stringify(input) }),

  updateSlaPolicy: (id: number, input: SlaPolicyInput) =>
    api<SlaPolicy>(`/api/contracts/sla-policies/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  previewSlaPolicy: (id: number) => api<SlaPolicyPreview>(`/api/contracts/sla-policies/${id}/preview`),

  activateSlaPolicy: (id: number, reason: string) =>
    api<SlaPolicy>(`/api/contracts/sla-policies/${id}/activate`, { method: 'POST', body: JSON.stringify({ reason }) }),

  deactivateSlaPolicy: (id: number) =>
    api<SlaPolicy>(`/api/contracts/sla-policies/${id}/deactivate`, { method: 'POST' }),

  deleteSlaPolicy: (id: number) => api<void>(`/api/contracts/sla-policies/${id}`, { method: 'DELETE' }),
}
